import { useCallback } from 'react';

import { useRouter } from 'expo-router';

import { useNavigation } from 'hooks/navigation/use-navigation';
import { useRoute } from 'hooks/navigation/use-route';
import { selectors, useAppSelector } from 'state/global-state';
import { useMemoAll } from 'utils/use-memo-all';

import { FlowStep, IProductWizardFlow, IProductWizardParams, ProductWizardStep } from './types';

import { useProductWizardContext } from './index';

/**
 * Controls product wizard steps navigation,
 * hiding implementation details about router and queryString manipulation.
 */

export const useProductWizardFlow = (): IProductWizardFlow => {
  const router = useRouter();
  const { goBack } = useNavigation();
  const { params } = useRoute<IProductWizardParams>();
  const appliedRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);

  const defaultInitialStep = params.cartIdEditing
    ? ProductWizardStep.PickerReview
    : ProductWizardStep.ProductHome;

  const {
    cartIdEditing = '',
    id = '',
    pickerAspectKey = '',
    slotKey = '',
    step = defaultInitialStep,
    rewardBenefitId,
    rewardId: rewardIdParam,
  } = params || {};

  const {
    flow,
    menuObject,
    saveSlotsSelectionSnapshot,
    clearSlotsSelectionSnapshot,
    hasUnsavedChanges,
    setShowCloseDialog,
    setFlowDirection,
  } = useProductWizardContext();

  const currentFlowStep = {
    pickerAspectKey,
    slotKey,
    step: step as ProductWizardStep,
  };

  const currentStepIndex = findCurrentStepIndex(flow, currentFlowStep);
  const nextFlowStep: FlowStep | undefined = flow[currentStepIndex + 1];

  const navigateToStep = useCallback(
    ({ pickerAspectKey = '', slotKey = '', step }: FlowStep) => {
      const updatedParams = {
        ...params,
        pickerAspectKey,
        slotKey,
        step,
      };
      router.setParams(updatedParams);
    },
    [params, router]
  );

  const goToNextStep = useCallback(() => {
    setFlowDirection('forward');
    navigateToStep(nextFlowStep);
  }, [navigateToStep, nextFlowStep, setFlowDirection]);

  const goToPreviousStep = useCallback(() => {
    setFlowDirection('backward');
    if (currentFlowStep.step === ProductWizardStep.ProductCustomization) {
      if (hasUnsavedChanges()) {
        setShowCloseDialog(true);
        return;
      }

      clearSlotsSelectionSnapshot();

      navigateToStep({
        step: flow.length ? ProductWizardStep.PickerReview : ProductWizardStep.ProductHome,
      });
      return;
    }

    const isInitialStep = currentFlowStep.step === ProductWizardStep.ProductHome || cartIdEditing;
    const previousFlowStep: FlowStep | undefined = isInitialStep
      ? undefined
      : flow[currentStepIndex - 1];

    if (previousFlowStep) {
      navigateToStep(previousFlowStep);
      return;
    }

    router.dismiss();
  }, [
    setFlowDirection,
    currentFlowStep.step,
    cartIdEditing,
    flow,
    currentStepIndex,
    router,
    hasUnsavedChanges,
    clearSlotsSelectionSnapshot,
    navigateToStep,
    setShowCloseDialog,
  ]);

  const goToCustomization = useCallback(
    (slotKeyToCustomize: string) => {
      setFlowDirection('forward');
      saveSlotsSelectionSnapshot();
      navigateToStep({
        step: ProductWizardStep.ProductCustomization,
        slotKey: slotKeyToCustomize,
      });
    },
    [navigateToStep, saveSlotsSelectionSnapshot, setFlowDirection]
  );

  const offerId =
    '__parentLoyaltyEntityId' in menuObject
      ? (menuObject.__parentLoyaltyEntityId as string)
      : undefined;

  let rewardId = rewardIdParam;
  if (!rewardIdParam && cartIdEditing && appliedRewards[cartIdEditing]) {
    rewardId = appliedRewards[cartIdEditing].sanityId;
  }

  return useMemoAll<IProductWizardFlow>({
    cartIdEditing,
    currentFlowStep: currentFlowStep as FlowStep,
    id,
    offerId,
    nextFlowStep,
    rewardBenefitId,
    rewardId,
    closeWizard: goBack,
    goToCustomization,
    goToNextStep,
    goToPreviousStep,
  });
};

function findCurrentStepIndex(flow: FlowStep[], { step, slotKey, pickerAspectKey }: FlowStep) {
  if (step === ProductWizardStep.ProductHome) {
    return 0;
  }
  return flow.findIndex(
    x =>
      x.step === step &&
      (!x.slotKey || x.slotKey === slotKey) &&
      (!x.pickerAspectKey || x.pickerAspectKey === pickerAspectKey)
  );
}
